import React, {useCallback, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {AccentWithDestructive, Alert, Button, SquareButton, Spinner} from "@tag/tag-components-react-v4";
import {
    ChevronRightFilled,
    DatabaseFilled,
    DocFilled,
    DownloadFilled,
    FolderFilled,
    HomeFilled,
    RefreshFilled
} from "@tag/tag-icons";
import DbBackupsService, {DbBackupItem, DbBackupListResult} from "../services/DbBackupsService";
import {ProjectAccents} from "../../General/config/projectAccents";
import styles from './DbBackups.module.scss';

const formatSize = (bytes?: number) => {
    if (bytes === undefined || bytes === null) return '';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unit = 0;
    while (size >= 1024 && unit < units.length - 1) {
        size /= 1024;
        unit++;
    }
    return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

const formatDate = (value?: string) => {
    if (!value) return '';
    return new Date(value).toLocaleString();
};

const DbBackups = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const prefix = searchParams.get('prefix') ?? '';

    const [data, setData] = useState<DbBackupListResult>();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState<string>();
    const [alert, setAlert] = useState<{type: 'error' | 'success', message: string}>();

    const setPrefix = (newPrefix: string) => {
        if (newPrefix) {
            setSearchParams({prefix: newPrefix});
        } else {
            setSearchParams({});
        }
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await DbBackupsService.list(prefix);
            setData(result);
        } catch (e: any) {
            setAlert({type: 'error', message: e.message || 'Failed to load backups'});
        } finally {
            setLoading(false);
        }
    }, [prefix]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleDownload = async (file: DbBackupItem) => {
        setDownloading(file.fullPath);
        try {
            const url = await DbBackupsService.getDownloadUrl(file.fullPath);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e: any) {
            setAlert({type: 'error', message: e.message || 'Failed to get download URL'});
        } finally {
            setDownloading(undefined);
        }
    };

    const breadcrumbSegments = prefix.split('/').filter(Boolean);

    const renderBreadcrumb = () => (
        <div className={styles.Breadcrumb}>
            <button
                className={styles.BreadcrumbItem}
                onClick={() => setPrefix('')}
                disabled={breadcrumbSegments.length === 0}
            >
                <HomeFilled /> Root
            </button>
            {breadcrumbSegments.map((segment, index) => {
                const target = breadcrumbSegments.slice(0, index + 1).join('/') + '/';
                const isLast = index === breadcrumbSegments.length - 1;
                return (
                    <React.Fragment key={target}>
                        <span className={styles.BreadcrumbSeparator}><ChevronRightFilled /></span>
                        <button
                            className={styles.BreadcrumbItem}
                            onClick={() => setPrefix(target)}
                            disabled={isLast}
                        >
                            {segment}
                        </button>
                    </React.Fragment>
                );
            })}
        </div>
    );

    const renderList = () => {
        if (!data || (data.folders.length === 0 && data.files.length === 0)) {
            return <div className={styles.Empty}>This folder is empty.</div>;
        }

        return (
            <div className={styles.List}>
                {data.folders.map((folder) => (
                    <div
                        key={folder.fullPath}
                        className={`${styles.Row} ${styles.RowClickable}`}
                        onClick={() => setPrefix(folder.fullPath)}
                    >
                        <span className={styles.RowIcon}><FolderFilled /></span>
                        <span className={styles.RowName}>{folder.name}</span>
                        <span className={styles.RowMeta} />
                        <span className={styles.RowIcon}><ChevronRightFilled /></span>
                    </div>
                ))}
                {data.files.map((file) => (
                    <div key={file.fullPath} className={styles.Row}>
                        <span className={styles.RowIcon}><DocFilled /></span>
                        <span className={styles.RowName}>{file.name}</span>
                        <span className={styles.RowMeta}>
                            {formatSize(file.sizeBytes)}{file.lastModified ? ` · ${formatDate(file.lastModified)}` : ''}
                        </span>
                        <SquareButton
                            accent={ProjectAccents.PeoplePlanner as AccentWithDestructive}
                            icon={downloading === file.fullPath
                                ? <Spinner wheelProps={{style: {height: '18px', width: '18px', backgroundSize: '18px'}}} />
                                : <DownloadFilled />}
                            disabled={downloading === file.fullPath}
                            onClick={() => handleDownload(file)}
                        />
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={styles.Container}>
            <div className={styles.Header}>
                <h1 className={styles.Title}><DatabaseFilled /> DB Backups</h1>
                <Button
                    accent={ProjectAccents.PeoplePlanner}
                    icon={<RefreshFilled />}
                    text="Refresh"
                    onClick={loadData}
                />
            </div>
            {alert ? (
                <div style={{marginBottom: '1em'}}>
                    <Alert type={alert.type} closeTransition="fade" onClosed={() => setAlert(undefined)}>{alert.message}</Alert>
                </div>
            ) : null}
            {renderBreadcrumb()}
            {loading ? <Spinner /> : renderList()}
        </div>
    );
};

export default DbBackups;
